/**
 * useSlugResolver - DB-First Single-Segment Resolution
 * 
 * Resolves single-segment URLs (/:slug) via real-time database queries.
 * This is the single source of truth for slug → entity resolution.
 * 
 * Priority Chain:
 * 1. Entity Priority: location (100) > route (90) > page (80) > topic (70) > publication (60) > profile (50)
 * 2. Locale Priority: Stored > Browser > EN > First
 * 
 * INV-2: URL slug is ALWAYS the authority
 * INV-3: Single indexed DB query (~10-20ms)
 */

import { useQuery } from '@tanstack/react-query';
import { useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { resolveExpertVanityUrl } from '@/lib/routing/expertLookup';
import { getPreferredLocale, selectByLocalePriority } from '@/lib/routing/localePriority';
import { useRouteEntitySafe } from '@/contexts/RouteEntityContext';
import { logger } from '@/utils/logger';

// ============================================================================
// TYPES
// ============================================================================

/**
 * All 6 routed entity types from localization_metadata
 */
export type RoutedSourceType = 'route' | 'page' | 'location' | 'publication' | 'profile' | 'topic';

/**
 * Resolution result - found
 */
export interface SlugResolutionFound {
  found: true;
  sourceType: RoutedSourceType;
  sourceId: string;
  locale: string;
  translatedSlug: string;
  /** For location entities: the location's primary language */
  mainLanguage?: string;
}

/**
 * Resolution result - not found
 */
export interface SlugResolutionNotFound {
  found: false;
}

/**
 * Union type for slug resolution
 */
export type SlugResolution = SlugResolutionFound | SlugResolutionNotFound;

/**
 * Hook return type
 */
export interface UseSlugResolverResult {
  /** Resolution result - undefined while loading, SlugResolution after */
  resolution: SlugResolution | undefined;
  isLoading: boolean;
  error: Error | null;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Entity type priority for collision resolution
 * Higher = more authoritative
 */
const ENTITY_PRIORITY: Record<RoutedSourceType, number> = {
  location: 100,
  route: 90,
  page: 80,
  topic: 70,
  publication: 60,
  profile: 50,
};

/**
 * Routed source types (excludes email_template, practice_area, faq)
 */
const ROUTED_SOURCE_TYPES = new Set<RoutedSourceType>([
  'route', 'page', 'location', 'publication', 'profile', 'topic'
]);

// ============================================================================
// CORE RESOLUTION FUNCTION
// ============================================================================

interface MetadataRow {
  source_type: string;
  source_id: string;
  locale: string;
  translated_slug: string;
}

/**
 * Resolve a slug to entity + locale via DB query
 */
async function resolveSlug(slug: string): Promise<SlugResolution> {
  const startTime = performance.now();
  
  if (!slug || slug.length === 0) {
    return { found: false };
  }

  try {
    // Query localization_metadata for all matches of this slug
    const { data: rows, error } = await supabase
      .from('localization_metadata')
      .select('source_type, source_id, locale, translated_slug')
      .eq('translated_slug', slug)
      .in('source_type', ['route', 'page', 'location', 'publication', 'topic', 'profile']);
    
    if (error) {
      logger.warn('[useSlugResolver] DB query failed', { slug, error: error.message });
      return { found: false };
    }

    if (!rows || rows.length === 0) {
      // Not found in metadata - might be an unseeded expert vanity URL
      const expertData = await resolveExpertVanityUrl(slug);
      if (expertData) {
        return {
          found: true,
          sourceType: 'profile',
          sourceId: expertData.userId,
          locale: getPreferredLocale(), // Profile pages don't change locale
          translatedSlug: slug,
        };
      }
      return { found: false };
    }

    // Filter to routed types only
    const routedRows = rows.filter(
      (r): r is MetadataRow => ROUTED_SOURCE_TYPES.has(r.source_type as RoutedSourceType)
    );

    if (routedRows.length === 0) {
      return { found: false };
    }

    // Apply entity priority (higher priority wins)
    const sortedByEntityPriority = routedRows.sort((a, b) => {
      const priorityA = ENTITY_PRIORITY[a.source_type as RoutedSourceType] || 0;
      const priorityB = ENTITY_PRIORITY[b.source_type as RoutedSourceType] || 0;
      return priorityB - priorityA;
    });

    // Get highest priority entity type
    const highestPriorityType = sortedByEntityPriority[0].source_type;
    const candidatesWithSameType = sortedByEntityPriority.filter(
      r => r.source_type === highestPriorityType
    );

    // For location entities with multiple locale candidates, fetch main_language
    // BEFORE locale selection so it can serve as a tiebreaker (priority 2.5)
    let mainLanguage: string | undefined;
    
    if (highestPriorityType === 'location' && candidatesWithSameType.length > 1) {
      const { data: locationData } = await supabase
        .from('locations')
        .select('main_language')
        .eq('location_id', parseInt(candidatesWithSameType[0].source_id, 10))
        .maybeSingle();
      
      mainLanguage = locationData?.main_language || undefined;
    }

    // Apply locale priority to determine which locale wins
    const selectedRow = selectByLocalePriority(candidatesWithSameType, {
      preferLocale: mainLanguage,
    });
    
    // For single-candidate locations, still fetch main_language for downstream use
    if (selectedRow.source_type === 'location' && !mainLanguage) {
      const { data: locationData } = await supabase
        .from('locations')
        .select('main_language')
        .eq('location_id', parseInt(selectedRow.source_id, 10))
        .maybeSingle();
      
      mainLanguage = locationData?.main_language || undefined;
    }

    if (process.env.NODE_ENV === 'development') {
      logger.debug('[useSlugResolver] Resolved', {
        slug,
        sourceType: selectedRow.source_type,
        sourceId: selectedRow.source_id,
        locale: selectedRow.locale,
        mainLanguage,
        candidateCount: routedRows.length,
        timeMs: Math.round(performance.now() - startTime),
      });
    }

    return {
      found: true,
      sourceType: selectedRow.source_type as RoutedSourceType,
      sourceId: selectedRow.source_id,
      locale: selectedRow.locale,
      translatedSlug: selectedRow.translated_slug,
      mainLanguage,
    };
  } catch (err) {
    logger.error('[useSlugResolver] Unexpected error', { slug, error: err });
    return { found: false };
  }
}

// Note: Locale priority helpers (getPreferredLocale, selectByLocalePriority)
// are imported from @/lib/routing/localePriority

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to resolve a single-segment slug to entity + locale
 * 
 * @param slug - The URL slug to resolve (e.g., 'brazil', 'experts', 'about-us')
 * @returns Resolution result with entity type, sourceId, and locale
 * 
 * @example
 * const { resolution, isLoading } = useSlugResolver(slug);
 * if (resolution?.found) {
 *   // Use resolution.sourceType, resolution.sourceId, resolution.locale
 * }
 */
export function useSlugResolver(slug: string | undefined): UseSlugResolverResult {
  const routeEntityContext = useRouteEntitySafe();
  const isLanguageSwitchPending = routeEntityContext?.isLanguageSwitchPending ?? false;
  
  // Track previous valid resolution to return during language switch
  const previousResolutionRef = useRef<SlugResolution | undefined>(undefined);

  const {
    data: resolution,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['slug-resolution', slug],
    queryFn: () => resolveSlug(slug || ''),
    // CRITICAL: Disable query during language switch to prevent race condition
    // where new locale is set but URL hasn't navigated yet
    enabled: !!slug && !isLanguageSwitchPending,
    staleTime: 5 * 60 * 1000, // 5 minutes - slugs don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Update ref when we get a valid resolution (not during language switch)
  useEffect(() => {
    if (resolution?.found && !isLanguageSwitchPending) {
      previousResolutionRef.current = resolution;
    }
  }, [resolution, isLanguageSwitchPending]);

  // Handle empty slug case (home page)
  if (!slug) {
    return {
      resolution: { found: false },
      isLoading: false,
      error: null,
    };
  }

  // CRITICAL: During language switch, return previous resolution to prevent 404 flash
  // The DeferredLanguageNavigator will handle navigation to the correct URL
  if (isLanguageSwitchPending && previousResolutionRef.current) {
    return {
      resolution: previousResolutionRef.current,
      isLoading: false,
      error: null,
    };
  }

  // CRITICAL: Don't default to { found: false } while loading
  // This was causing NotFound to flash before actual resolution
  return {
    resolution: isLoading ? undefined : (resolution || { found: false }),
    isLoading,
    error: error as Error | null,
  };
}

// ============================================================================
// IMPERATIVE API (for non-React contexts)
// ============================================================================

/**
 * Imperative slug resolution for use outside React components
 * (e.g., in LocaleContext initialization)
 */
export async function fetchSlugResolution(slug: string): Promise<SlugResolution> {
  return resolveSlug(slug);
}

/**
 * @deprecated Use fetchSlugResolution instead
 */
export const resolveSlugImperative = fetchSlugResolution;
